import React, { FC, useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Global } from "./constants";
// import { ConfigProvider } from "antd"; // Removed as it's now handled in ThemeProvider
import { ThemeProvider } from "./context/ThemeContext";
import { useNoCache } from "./hooks/useNoCache.hook";

import NotFound from "./pages/404.page";
import SuspenseWrapper from "./hoc/suspense.hoc";
import ProtectedRoute from "./hoc/protected-routes.hoc";
import AuthLayout from "./components/layouts/auth-layout.component";
import AdminLayout from "./components/layouts/admin-layout.component";
import CustomLayout from "./components/layouts/custom-layout.component";

import "./scss/main.sass";

type Props = {
  isLoggedIn?: any;
};

const App: FC<Props> = ({ isLoggedIn }) => {
  // Clear browser caches on load and ensure fresh chunks/assets
  useNoCache();
  const location = useLocation();
  const navigate = useNavigate();
  const ProfilePage = React.lazy(() => import("./pages/admin/profile.page"));
  const HomePage = React.lazy(() => import("./pages/admin/home.page"));
  const CompaniesPage = React.lazy(() => import("./pages/admin/branchs.page"));
  const VehiclesPage = React.lazy(() => import("./pages/admin/vehicles.page"));
  const VehicleDetailPage = React.lazy(
    () => import("./pages/admin/vehicle-detail.page"),
  );
  const VehicleLoansPage = React.lazy(() => import("./pages/admin/vehicle-loans.page"));
  const ActivesPage = React.lazy(() => import("./pages/admin/actives.page"));
const ActiveLoansPage = React.lazy(() => import("./pages/admin/active-loans.page"));
  const AdminsPage = React.lazy(() => import("./pages/admin/admins.page"));
  const RolesPage = React.lazy(() => import("./pages/admin/roles.page"));
  const TemplatesPage = React.lazy(
    () => import("./pages/admin/templates.page"),
  );
  const LoginPage = React.lazy(() => import("./pages/login.page"));
  const DocumentsPage = React.lazy(
    () => import("./pages/admin/documents.page"),
  );
  const ReportsPage = React.lazy(() => import("./pages/admin/reports.page"));
  const DocumentCreatorPage = React.lazy(
    () => import("./pages/admin/document-creator.page"),
  );
  const VoboPage = React.lazy(() => import("./pages/vobo.page"));
  const EmployeeStatusPage = React.lazy(
    () => import("./pages/employee-status.page"),
  );
  const DocumentDetail = React.lazy(
    () => import("./pages/admin/document-detail.page"),
  );
  const ResetPasswordPage = React.lazy(
    () => import("./pages/reset-password.page"),
  );
  const ForgotPasswordPage = React.lazy(
    () => import("./pages/forgot-password.page"),
  );
  const GenerateTokenPage = React.lazy(
    () => import("./pages/generate-token.page"),
  );
  const ProyectsPage = React.lazy(() => import("./pages/admin/proyects.page"));
  const ProductsPage = React.lazy(() => import("./pages/admin/products.page"));
  const InvoicesPage = React.lazy(() => import("./pages/admin/invoices.page"));
  const CartaPortePage = React.lazy(() => import("./pages/admin/carta-porte.page"));
  const ShoppingPage = React.lazy(() => import("./pages/admin/shoppings.page"));
  const StorePage = React.lazy(() => import("./pages/admin/store.page"));
  const ShoppingsV2Page = React.lazy(
    () => import("./pages/admin/shoppings-v2.page"),
  );
  const ShoppingsDetailV2Page = React.lazy(
    () => import("./pages/admin/shoppings-detail-v2.page"),
  );
  const PendingProductsPage = React.lazy(
    () => import("./pages/admin/pending-products.page"),
  );
  const WarehousesPage = React.lazy(
    () => import("./pages/admin/warehouses.page"),
  );
  const SeminuevosPage = React.lazy(
    () => import("./pages/admin/seminuevos.page"),
  );
  const EmployeesPage = React.lazy(
    () => import("./pages/admin/employees.page"),
  );
  const EmployeeQrPage = React.lazy(
    () => import("./pages/admin/employee-qr.page"),
  );
  const NotificationsPage = React.lazy(
    () => import("./pages/admin/notifications.page"),
  );
  const MovementsPage = React.lazy(
    () => import("./pages/admin/movements.page"),
  );
  const TransfersPage = React.lazy(
    () => import("./pages/admin/transfers.page"),
  );
  const StockModificationsPage = React.lazy(
    () => import("./pages/admin/stock-modifications.page"),
  );
  const FormatsPage = React.lazy(() => import("./pages/admin/formats.page"));
  const AdministracionPage = React.lazy(() => import("./pages/admin/administracion.page"));
  const AdministracionEmpresaPage = React.lazy(() => import("./pages/admin/administracion-empresa.page"));
  const PayrollPage = React.lazy(() => import("./pages/admin/payroll.page"));
  const EmployeesExpensesPage = React.lazy(
    () => import("./pages/admin/employees-expenses.page"),
  );
  const LoansPage = React.lazy(() => import("./pages/admin/loans.page"));
  const EmployeeWalletPage = React.lazy(
    () => import("./pages/admin/employee-wallet.page"),
  );
  const BonosPage = React.lazy(() => import("./pages/admin/bonos.page"));
  const CustomerQuotationsPage = React.lazy(
    () => import("./pages/admin/customer-quotations.page"),
  );
  const NovedadesPage = React.lazy(
    () => import("./pages/admin/novedades.page"),
  );
  const WarrantiesPage = React.lazy(
    () => import("./pages/admin/warranties.page"),
  );
  const ToolsCatalogPage = React.lazy(
    () => import("./pages/admin/tools-catalog.page"),
  );
  const ToolsStockPage = React.lazy(
    () => import("./pages/admin/tools-stock.page"),
  );
  const ToolsAdjustmentsPage = React.lazy(
    () => import("./pages/admin/tools-adjustments.page"),
  );
  const ToolsMovementsPage = React.lazy(
    () => import("./pages/admin/tools-movements.page"),
  );
  const EmployeeDiscountsPage = React.lazy(
    () => import("./pages/admin/employee-discounts.page"),
  );
  const WarrantyTicketsPage = React.lazy(
    () => import("./pages/admin/warranty-tickets.page"),
  );
  const BitacorasPage = React.lazy(
    () => import("./pages/admin/bitacoras.page"),
  );
  const ProyectCreatorPage = React.lazy(
    () => import("./pages/admin/proyect-creator.page"),
  );
  const ProyectEditorPage = React.lazy(
    () => import("./pages/admin/proyect-editor.page"),
  );
  const ProyectDetail = React.lazy(
    () => import("./pages/admin/proyect-detail.page"),
  );

  useEffect(() => {
    const unprotectedRoutes = Global.UNPROTECTED_ROUTES;
    const containsUnprotectedRoutes = unprotectedRoutes.filter((path) =>
      location.pathname.startsWith(path),
    );
    if (!isLoggedIn && containsUnprotectedRoutes.length === 0) {
      navigate(Global.APP_URLS.LOGIN);
    }
  }, [isLoggedIn, location.pathname, navigate]);

  return (
    <ThemeProvider>
      <SuspenseWrapper>
        <ProtectedRoute route={location.pathname}>
          <>
            <Routes>
              <Route path="/" element={<AuthLayout />}>
                <Route index element={<LoginPage />} />
              </Route>
              <Route path="/auth" element={<AuthLayout />}>
                <Route
                  path="/auth/reset-password"
                  element={<ResetPasswordPage />}
                />
                <Route path="/auth/forgot" element={<ForgotPasswordPage />} />
                <Route
                  path="/auth/generateToken"
                  element={<GenerateTokenPage />}
                />
              </Route>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<HomePage />} />
                <Route path="/admin/profile" element={<ProfilePage />} />
                <Route path="/admin/companies" element={<CompaniesPage />} />
                <Route path="/admin/vehicles" element={<VehiclesPage />} />
                <Route
                  path="/admin/vehicles/:vehicleId"
                  element={<VehicleDetailPage />}
                />
                <Route path="/admin/vehicle-loans" element={<VehicleLoansPage />} />
                <Route path={Global.APP_URLS.ACTIVES} element={<ActivesPage />} />
                <Route path={Global.APP_URLS.ACTIVE_LOANS} element={<ActiveLoansPage />} />
                <Route path={Global.APP_URLS.ADMINS} element={<AdminsPage />} />
                <Route path={Global.APP_URLS.ROLES} element={<RolesPage />} />
                <Route path="/admin/templates" element={<TemplatesPage />} />

                <Route path="/admin/documents" element={<DocumentsPage />} />
                <Route
                  path="/admin/document/creator"
                  element={<DocumentCreatorPage />}
                />
                <Route
                  path="/admin/document/:documentId"
                  element={<DocumentDetail />}
                />

                <Route path="/admin/proyects" element={<ProyectsPage />} />
                <Route
                  path="/admin/proyect/creator"
                  element={<ProyectCreatorPage />}
                />
                <Route
                  path="/admin/proyect/editor/:proyectId"
                  element={<ProyectEditorPage />}
                />
                <Route
                  path="/admin/proyect/:proyectId"
                  element={<ProyectDetail />}
                />

                <Route path="/admin/products" element={<ProductsPage />} />
                <Route path="/admin/invoices" element={<InvoicesPage />} />
                <Route path="/admin/carta-porte" element={<CartaPortePage />} />
                <Route path="/admin/shoppings" element={<ShoppingPage />} />
                <Route
                  path="/admin/shoppings/:proyectId"
                  element={<ShoppingPage />}
                />
                <Route
                  path="/admin/shoppings-v2"
                  element={<ShoppingsV2Page />}
                />
                <Route
                  path="/admin/shoppings-v2/:proyectId"
                  element={<ShoppingsDetailV2Page />}
                />
                <Route path="/admin/store" element={<StorePage />} />
                <Route
                  path="/admin/tools/catalog"
                  element={<ToolsCatalogPage />}
                />
                <Route
                  path="/admin/tools/stock"
                  element={<ToolsStockPage />}
                />
                <Route
                  path="/admin/tools/adjustments"
                  element={<ToolsAdjustmentsPage />}
                />
                <Route
                  path="/admin/tools/movements"
                  element={<ToolsMovementsPage />}
                />
                <Route
                  path="/admin/employee-discounts"
                  element={<EmployeeDiscountsPage />}
                />
                <Route
                  path="/admin/store/pending-products"
                  element={<PendingProductsPage />}
                />
                <Route
                  path="/admin/store/warehouses"
                  element={<WarehousesPage />}
                />
                <Route
                  path="/admin/store/seminuevos"
                  element={<SeminuevosPage />}
                />
                <Route
                  path="/admin/store/movements"
                  element={<MovementsPage />}
                />
                <Route
                  path="/admin/store/transfers"
                  element={<TransfersPage />}
                />
                <Route
                  path="/admin/store/stock-modifications"
                  element={<StockModificationsPage />}
                />
                <Route path="/admin/employees" element={<EmployeesPage />} />
                <Route
                  path="/admin/employees/credentials"
                  element={<EmployeeQrPage />}
                />
                <Route
                  path="/admin/employees-expenses"
                  element={<EmployeesExpensesPage />}
                />
                <Route path="/admin/loans" element={<LoansPage />} />
                <Route path="/admin/wallet" element={<EmployeeWalletPage />} />
                <Route path="/admin/bonos" element={<BonosPage />} />
                <Route
                  path={Global.APP_URLS.CUSTOMER_QUOTATIONS}
                  element={<CustomerQuotationsPage />}
                />
                <Route path="/admin/payrolls" element={<PayrollPage />} />
                <Route path="/admin/administracion" element={<AdministracionPage />} />
                <Route path="/admin/administracion/:company" element={<AdministracionEmpresaPage />} />
                <Route
                  path="/admin/notifications"
                  element={<NotificationsPage />}
                />
                <Route path="/admin/novedades" element={<NovedadesPage />} />
                <Route
                  path={Global.APP_URLS.WARRANTIES}
                  element={<WarrantiesPage />}
                />
                <Route
                  path={Global.APP_URLS.WARRANTY_TICKETS}
                  element={<WarrantyTicketsPage />}
                />
                <Route
                  path={`${Global.APP_URLS.WARRANTY_TICKETS}/:ticketFolio`}
                  element={<WarrantyTicketsPage />}
                />
                <Route
                  path={Global.APP_URLS.BITACORAS}
                  element={<BitacorasPage />}
                />

                <Route path="/admin/reports" element={<ReportsPage />} />
                <Route
                  path="/admin/settings/formats"
                  element={<FormatsPage />}
                />
              </Route>
              <Route path="/document" element={<CustomLayout />}>
                <Route
                  path="/document/vobo/:documentId"
                  element={<VoboPage />}
                />
              </Route>
              {/* Credencial QR pública del empleado (estatus en vivo, sin login) */}
              <Route path="/qr" element={<CustomLayout />}>
                <Route
                  path="/qr/empleado/:qrToken"
                  element={<EmployeeStatusPage />}
                />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </>
        </ProtectedRoute>
      </SuspenseWrapper>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: any) => ({
  isLoggedIn: state.auth?.login || null,
});

export default connect(mapStateToProps, null)(App);
